import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, UsersThree, WifiSlash } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { fmtInTz } from '../../lib/tz';
import { NightBar } from '../movement/NightBar';
import { VerdictChip, nightSentence, durText } from '../movement/nightVerdict';

export function SubjectTitle({ subject, testid }) {
  const { t } = useI18n();
  return (
    <div className="min-w-0">
      <Link to={subject.to} className="font-heading text-lg text-ink hover:text-sage-700 inline-flex items-center gap-2" data-testid={testid}>
        {subject.kind === 'room' && <UsersThree size={16} weight="duotone" className="text-ink-soft" />}
        {subject.name} <ArrowRight size={14} className="text-ink-muted" />
      </Link>
      {subject.kind === 'room' && (
        <div className="text-[11px] text-ink-soft" data-testid={`${testid}-shared`}>
          {t('rep_shared_room_row', { names: subject.members.join(', ') })}
        </div>
      )}
    </div>
  );
}

const Fact = ({ label, children, testid }) => (
  <span data-testid={testid}><span className="text-ink-muted">{label}</span> <span className="text-ink">{children}</span></span>
);

// One subject's night: verdict chip, the night as a colour bar, one sentence, key times.
export function NightRow({ subject, night, buckets }) {
  const { t, lang } = useI18n();
  const { id } = subject;
  const tz = night?.window.tz;
  const hm = (ms) => fmtInTz(ms, tz, { hour: '2-digit', minute: '2-digit' }, lang);
  const s = night?.summary;

  return (
    <div className="bg-white border border-stone_line rounded-2xl shadow-soft p-5 sm:p-6" data-testid={`night-row-${id}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <SubjectTitle subject={subject} testid={`night-row-link-${id}`} />
        {night && <VerdictChip kind={night.kind} t={t} testid={`night-verdict-${id}`} />}
      </div>
      {!night ? (
        <div className="h-8 rounded-md bg-bg-alt animate-pulse" />
      ) : (
        <>
          <NightBar buckets={buckets} ids={subject.ids} win={night.window} testid={`night-bar-${id}`} />
          <p className="text-sm text-ink mt-3 leading-relaxed" data-testid={`night-sentence-${id}`}>{nightSentence(t, night, hm)}</p>
          {night.kind === 'nodata' ? <span className="sr-only" data-testid={`night-nodata-${id}`} /> : (
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs mt-3 pt-3 border-t border-stone_line">
              <Fact label={t('rep_first_movement')} testid={`night-first-${id}`}>{s.firstMovementMs != null ? hm(s.firstMovementMs) : '—'}</Fact>
              <Fact label={t('mv_last_movement')} testid={`night-last-${id}`}>{s.lastMovementIso ? hm(new Date(s.lastMovementIso).getTime()) : '—'}</Fact>
              <Fact label={t('rep_longest_still')} testid={`night-still-${id}`}>
                {s.longestStill ? `${durText(t, s.longestStill.minutes)} (${hm(s.longestStill.fromMs)}–${hm(s.longestStill.toMs)})` : '—'}
              </Fact>
              <Fact label={t('rep_silences')} testid={`night-silences-${id}`}>
                {s.silences.length === 0 ? t('rep_none') : (
                  <span className="inline-flex items-center gap-1"><WifiSlash size={12} />{s.silences.map((g) => `${hm(g.fromMs)}–${hm(g.toMs)}`).join(', ')}</span>
                )}
              </Fact>
              <span className="text-ink-muted">{t('rep_coverage', { pct: Math.round(s.coverage * 100) })}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
